import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSelectedProviders } from '../hooks/useSelectedProviders';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Download, FileText, FileSpreadsheet, Calendar, Cloud,
  RefreshCw, Filter, TrendingUp, FileDown
} from 'lucide-react';
import { 
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_COLORS = {
  'AWS': '#FF9900',
  'Azure': '#0089D6',
  'GCP': '#4285F4',
  'Snowflake': '#29B5E8',
  'Databricks': '#FF3621',
  'DigitalOcean': '#0080FF',
  'Other': '#64748B'
};

const Reports = () => {
  const { shouldShowData } = useDemoData();

  const { user } = useAuth();
  const { providerDetails, shouldShowAll } = useSelectedProviders();
  const [loading, setLoading] = useState(true);
  const [providerData, setProviderData] = useState([]);
  const [filteredCosts, setFilteredCosts] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    provider: '',
    service: '',
    minCost: '',
    maxCost: ''
  });

  useEffect(() => {
    fetchProviderData();
  }, []);

  const fetchProviderData = async () => {
    try {
      const response = await axios.get(`${API_URL}/analytics/by-provider`);
      setProviderData(response.data);
    } catch (error) {
      console.error('Failed to fetch provider data:', error);
      toast.error('Failed to load provider analytics');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.provider && filters.provider !== 'all') params.append('provider', filters.provider);
      if (filters.service) params.append('service', filters.service);
      if (filters.minCost) params.append('min_cost', filters.minCost);
      if (filters.maxCost) params.append('max_cost', filters.maxCost);
      
      const response = await axios.get(`${API_URL}/costs/filtered?${params.toString()}`);
      setFilteredCosts(response.data);
      
      // Also refresh provider data with date filter
      const providerParams = new URLSearchParams();
      if (filters.startDate) providerParams.append('start_date', filters.startDate);
      if (filters.endDate) providerParams.append('end_date', filters.endDate);
      const providerResponse = await axios.get(`${API_URL}/analytics/by-provider?${providerParams.toString()}`);
      setProviderData(providerResponse.data);
      
      toast.success('Filters applied');
    } catch (error) {
      toast.error('Failed to apply filters');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      provider: '',
      service: '',
      minCost: '',
      maxCost: ''
    });
    setFilteredCosts(null);
    fetchProviderData();
    toast.success('Filters cleared');
  };

  const exportCSV = async (type) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.provider && filters.provider !== 'all') params.append('provider', filters.provider);
      
      const endpoint = type === 'costs' ? '/reports/costs/csv' : '/reports/summary/csv';
      const response = await axios.get(`${API_URL}${endpoint}?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cartieai_${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${type === 'costs' ? 'Cost data' : 'Summary report'} exported successfully`);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const exportPDF = async () => {
    setExportingPdf(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      
      const response = await axios.get(`${API_URL}/reports/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cartieai_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF report exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Prepare chart data
  const pieChartData = providerData.map(p => ({
    name: p.provider,
    value: p.total_cost,
    percentage: p.percentage
  }));

  const barChartData = providerData.flatMap(p => 
    p.services.slice(0, 3).map(s => ({
      service: s.service.length > 15 ? s.service.substring(0, 15) + '...' : s.service,
      cost: s.cost,
      provider: p.provider
    }))
  ).sort((a, b) => b.cost - a.cost).slice(0, 10);

  if (!shouldShowData) {
    return <EmptyDataState title="Reports" description="Connect your cloud account to see real data here." />;
  }

  if (loading && !filteredCosts) {
    return (
      <div className="p-6 space-y-6" data-testid="reports-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="p-6">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white">
            Reports & Analytics
          </h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            Multi-cloud cost analytics for AWS, Azure, GCP, Snowflake, Databricks & DigitalOcean with custom date filtering and exports
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline"
            onClick={() => exportCSV('costs')}
            disabled={exporting}
            data-testid="export-costs-btn"
            className="gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" />
            CSV Costs
          </Button>
          <Button 
            variant="outline"
            onClick={() => exportCSV('summary')}
            disabled={exporting}
            data-testid="export-summary-btn"
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            CSV Summary
          </Button>
          <Button 
            onClick={exportPDF}
            disabled={exportingPdf}
            data-testid="export-pdf-btn"
            className="gap-2 bg-emerald-600 hover:bg-emerald-700"
          >
            {exportingPdf ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            PDF Report
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="filters-card">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-emerald-600" />
            <CardTitle className="text-lg font-semibold font-['Manrope']">Filters</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                data-testid="filter-end-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="provider">Cloud Provider</Label>
              <Select 
                value={filters.provider} 
                onValueChange={(value) => setFilters({ ...filters, provider: value })}
              >
                <SelectTrigger data-testid="filter-provider">
                  <SelectValue placeholder="All Providers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Providers</SelectItem>
                  {shouldShowAll ? (
                    <>
                      <SelectItem value="AWS">AWS</SelectItem>
                      <SelectItem value="Azure">Azure</SelectItem>
                      <SelectItem value="GCP">GCP</SelectItem>
                      <SelectItem value="Snowflake">Snowflake</SelectItem>
                      <SelectItem value="Databricks">Databricks</SelectItem>
                      <SelectItem value="DigitalOcean">DigitalOcean</SelectItem>
                    </>
                  ) : (
                    providerDetails.map((p) => (
                      <SelectItem key={p.id} value={p.name}>
                        {p.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="service">Service</Label>
              <Input
                id="service"
                placeholder="e.g., EC2"
                value={filters.service}
                onChange={(e) => setFilters({ ...filters, service: e.target.value })}
                data-testid="filter-service"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="minCost">Min Cost ($)</Label>
              <Input
                id="minCost"
                type="number"
                placeholder="0"
                value={filters.minCost}
                onChange={(e) => setFilters({ ...filters, minCost: e.target.value })}
                data-testid="filter-min-cost"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="maxCost">Max Cost ($)</Label>
              <Input
                id="maxCost"
                type="number"
                placeholder="10000"
                value={filters.maxCost}
                onChange={(e) => setFilters({ ...filters, maxCost: e.target.value })}
                data-testid="filter-max-cost"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={applyFilters} className="bg-emerald-600 hover:bg-emerald-700" data-testid="apply-filters-btn">
              Apply Filters
            </Button>
            <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filter Results Summary */}
      {filteredCosts && (
        <Card data-testid="filter-results">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <Badge variant="outline" className="text-sm">
                {filteredCosts.summary.record_count} records
              </Badge>
              <Badge variant="outline" className="text-sm bg-emerald-50 text-emerald-700 border-emerald-200">
                Total: {formatCurrency(filteredCosts.summary.total_cost)}
              </Badge>
              <Badge variant="outline" className="text-sm">
                Avg: {formatCurrency(filteredCosts.summary.average_cost)}
              </Badge>
              {filteredCosts.summary.date_range.start && (
                <Badge variant="outline" className="text-sm">
                  {filteredCosts.summary.date_range.start} to {filteredCosts.summary.date_range.end || 'Now'}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Multi-Cloud Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Provider Breakdown Pie Chart */}
        <Card data-testid="provider-pie-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Cost by Cloud Provider</CardTitle>
            <CardDescription>Distribution across AWS, Azure, GCP, Snowflake, Databricks & DigitalOcean</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PROVIDER_COLORS[entry.name] || PROVIDER_COLORS['Other']} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Services Bar Chart */}
        <Card data-testid="services-bar-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Top Services by Cost</CardTitle>
            <CardDescription>Highest spending services across all providers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                <BarChart data={barChartData} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <YAxis dataKey="service" type="category" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="cost" radius={[0, 4, 4, 0]}>
                    {barChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PROVIDER_COLORS[entry.provider] || PROVIDER_COLORS['Other']} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Provider Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {providerData.map((provider, idx) => (
          <Card key={idx} className="card-hover" data-testid={`provider-card-${provider.provider}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${PROVIDER_COLORS[provider.provider]}20` }}
                  >
                    <Cloud className="w-5 h-5" style={{ color: PROVIDER_COLORS[provider.provider] }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">{provider.provider}</h3>
                    <p className="text-sm text-slate-400">{provider.percentage}% of total</p>
                  </div>
                </div>
                <Badge 
                  style={{ 
                    backgroundColor: `${PROVIDER_COLORS[provider.provider]}20`,
                    color: PROVIDER_COLORS[provider.provider],
                    borderColor: PROVIDER_COLORS[provider.provider]
                  }}
                >
                  {provider.services.length} services
                </Badge>
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                {formatCurrency(provider.total_cost)}
              </p>
              <div className="space-y-2">
                {provider.services.slice(0, 3).map((service, sidx) => (
                  <div key={sidx} className="flex justify-between text-sm">
                    <span className="text-slate-400 truncate max-w-[150px]">{service.service}</span>
                    <span className="font-medium text-slate-900 dark:text-white">{formatCurrency(service.cost)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Reports;
